import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SoftwareTalent, SearchFilters } from '../types';
import { filterContractOnlyCompanies } from '../utils/credibilityLine';
import { SearchX, MapPin, Trophy, Award, Heart, Ban, RotateCcw, Eye, Github } from 'lucide-react';
import { saveTalent, discardTalent, restoreTalent, unsaveTalent, DomainType } from '../services/api';
import { CompanyLogo, UniversityLogo } from './BrandLogos';
import { getDomainConfig, getDomainLabel } from '../config/appConfig';
import { trackEvent } from '../utils/mixpanel';
import './SearchResults.css';

const TOP_GITHUB_CONTRIBUTOR_IDS = new Set([6798943, 6799081, 6799203]);

interface SearchPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface SearchResultsProps {
  talent: SoftwareTalent[];
  loading: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: (domain?: string) => void;
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  pagination?: SearchPagination | null;
  /** Terms to highlight (skill names, programming languages from parsed filters) */
  resultsHighlightTerms?: string[];
  /** Set of saved talent IDs */
  savedIds?: Set<number>;
  /** Set of viewed talent IDs */
  viewedIds?: Set<number>;
  /** Set of discarded talent IDs */
  discardedIds?: Set<number>;
  /** Callback when a talent is saved */
  onSave?: (talentId: number, domain?: DomainType) => void;
  /** Callback when a talent is unsaved (removed from saved list) */
  onUnsave?: (talentId: number, domain?: DomainType) => void;
  /** Callback when a talent is discarded */
  onDiscard?: (talentId: number, domain?: DomainType) => void;
  /** Callback when a talent is restored */
  onRestore?: (talentId: number, domain?: DomainType) => void;
  /** Current active tab */
  activeTab?: 'search' | 'saved' | 'viewed' | 'discarded';
  /** Callback when a card is clicked (for inline detail view) */
  onCardClick?: (talent: SoftwareTalent, domainFilter?: string) => void;
  /** Default domain filter for action tabs (from last search domain) */
  defaultDomainFilter?: string;
  /** Per-domain counts from API pagination.total (for action tab pills) */
  countsByDomain?: Record<string, number>;
  /** Per-domain hasMore flags (for action tab pagination) */
  hasMoreByDomain?: Record<string, boolean>;
  /** List of available domains */
  availableDomains?: string[];
  /** Whether multiple domains are enabled */
  multiDomainEnabled?: boolean;
}

export interface DeveloperCardProps {
  talent: SoftwareTalent;
  index: number;
  onClick: (talent: SoftwareTalent) => void;
  highlightTerms?: string[];
  isSaved?: boolean;
  isViewed?: boolean;
  isDiscarded?: boolean;
  onSave?: (talentId: number, domain?: DomainType) => void;
  onUnsave?: (talentId: number, domain?: DomainType) => void;
  onDiscard?: (talentId: number, domain?: DomainType) => void;
  onRestore?: (talentId: number, domain?: DomainType) => void;
  activeTab?: 'search' | 'saved' | 'viewed' | 'discarded';
  domain?: DomainType;
}

const HIGHLIGHT_CLASS = 'bg-yellow-200 text-gray-900 dark:bg-amber-600 dark:text-white';

/** Wraps matching substrings in <mark> in title, summary, skills, companies. Case-insensitive. Longer terms first. */
function highlightFragments(text: string, terms: string[]): React.ReactNode {
  if (!text || terms.length === 0) return text;
  const escaped = terms
    .filter(t => t && t.trim().length > 0)
    .map(t => t.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .sort((a, b) => b.length - a.length);
  if (escaped.length === 0) return text;
  const re = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(re);
  return parts.map((part, i) =>
    i % 2 === 1 ? React.createElement('mark', { key: i, className: HIGHLIGHT_CLASS }, part) : part
  );
}

/** Format large numbers: 1500 → "1.5k", 4500 → "4.5k" */
function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return n.toString();
}

/** Max skills in dev card; overflow shown as "+N" when they don't fit in first row. */
const MAX_SKILLS_DISPLAY = 5;
const CHIP_GAP_PX = 4;
const SINGLE_LINE_HEIGHT_PX = 22;
const MEASURE_BUFFER_PX = 4;

/** Renders text with ellipsis and a styled tooltip (matching the verified-profile look) only when truncated. */
const TruncatedText: React.FC<{
  text: string;
  className?: string;
}> = ({ text, className = '' }) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const handleMouseEnter = useCallback(() => {
    const el = spanRef.current;
    setIsTruncated(!!el && el.scrollWidth > el.clientWidth);
  }, []);

  return (
    <span className="group/uni relative min-w-0">
      <span
        ref={spanRef}
        className={`block overflow-hidden text-ellipsis whitespace-nowrap ${className}`}
        onMouseEnter={handleMouseEnter}
      >
        {text}
      </span>
      {isTruncated && (
        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 px-2 py-1 text-[11px] font-medium text-white bg-zinc-900 dark:bg-slate-700 rounded-md opacity-0 invisible group-hover/uni:opacity-100 group-hover/uni:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50 shadow-lg">
          {text}
          <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-zinc-900 dark:border-t-slate-700" />
        </span>
      )}
    </span>
  );
};

export const DeveloperCard: React.FC<DeveloperCardProps> = React.memo(({
  talent, 
  index, 
  onClick, 
  highlightTerms = [],
  isSaved = false,
  isViewed = false,
  isDiscarded = false,
  onSave,
  onUnsave,
  onDiscard,
  onRestore,
  activeTab = 'search',
  domain = 'SWE',
}) => {
  const [visibleCount, setVisibleCount] = useState(MAX_SKILLS_DISPLAY);
  const [skillsExpanded] = useState(false);
  const skillsRowRef = useRef<HTMLDivElement>(null);
  const [actionLoading, setActionLoading] = useState<'save' | 'unsave' | 'discard' | 'restore' | null>(null);

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (actionLoading) return;
    setActionLoading('save');
    try {
      await saveTalent(talent.id, domain);
      onSave?.(talent.id, domain);
    } catch (err) {
      console.error('Failed to save talent:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDiscard = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (actionLoading) return;
    setActionLoading('discard');
    try {
      await discardTalent(talent.id, domain);
      onDiscard?.(talent.id, domain);
    } catch (err) {
      console.error('Failed to discard talent:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestore = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (actionLoading) return;
    setActionLoading('restore');
    try {
      await restoreTalent(talent.id, domain);
      onRestore?.(talent.id, domain);
    } catch (err) {
      console.error('Failed to restore talent:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnsave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (actionLoading) return;
    setActionLoading('unsave');
    try {
      await unsaveTalent(talent.id, domain);
      onUnsave?.(talent.id, domain);
    } catch (err) {
      console.error('Failed to unsave talent:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const normalizeSkill = (skill: string | { name?: string }): string => {
    if (typeof skill === 'string') return skill;
    return skill?.name?.trim() || 'Unknown';
  };

  /** Prefer tSkills (generated/curated) over skills when available. */
  const rawSkills = (talent.tSkills ?? talent.skills ?? []) as (string | { name?: string })[];
  const allSkills = rawSkills.map(normalizeSkill).filter(Boolean) as string[];
  const isSkillHighlighted = React.useCallback(
    (skill: string) =>
      highlightTerms.some((t) => t.trim() && skill.toLowerCase().includes(t.trim().toLowerCase())),
    [highlightTerms]
  );
  /** Rank: highlighted first, then rest. Cap at 5. */
  const rankedSkills = React.useMemo(() => {
    const relevant = allSkills.filter(isSkillHighlighted);
    const others = allSkills.filter((s) => !isSkillHighlighted(s));
    return [...relevant, ...others].slice(0, MAX_SKILLS_DISPLAY);
  }, [allSkills, isSkillHighlighted]);

  /** Measure how many skills fit in first row; if not all fit, reserve space for "+N" chip. Skip when expanded. */
  useLayoutEffect(() => {
    const el = skillsRowRef.current;
    if (!el || rankedSkills.length === 0 || skillsExpanded) return;
    const measureChips = (availableWidth: number) => {
      const cs = getComputedStyle(el);
      const measure = document.createElement('div');
      measure.style.cssText = `position:absolute;visibility:hidden;top:-9999px;left:0;width:${availableWidth}px;display:flex;flex-wrap:wrap;gap:${CHIP_GAP_PX}px;align-items:center;font-size:12px;font-weight:500;font-family:${cs.fontFamily};line-height:1;`;
      document.body.appendChild(measure);
      let count = 0;
      for (const skill of rankedSkills) {
        const chip = document.createElement('span');
        chip.style.cssText = 'padding:0 7px;height:22px;display:inline-flex;align-items:center;white-space:nowrap;box-sizing:border-box;';
        chip.textContent = skill;
        measure.appendChild(chip);
        if (measure.offsetHeight > SINGLE_LINE_HEIGHT_PX) {
          measure.removeChild(chip);
          break;
        }
        count++;
      }
      document.body.removeChild(measure);
      return count;
    };
    const measureOverflowChip = (n: number) => {
      const cs = getComputedStyle(el);
      const chip = document.createElement('span');
      chip.style.cssText = `padding:0 7px;height:22px;display:inline-flex;align-items:center;white-space:nowrap;font-size:12px;font-weight:500;font-family:${cs.fontFamily};`;
      chip.textContent = `+${n}`;
      document.body.appendChild(chip);
      const w = chip.offsetWidth;
      document.body.removeChild(chip);
      return w;
    };
    const updateCount = () => {
      const rowWidth = el.offsetWidth;
      const countNoReserve = measureChips(rowWidth);
      if (countNoReserve >= rankedSkills.length) {
        setVisibleCount(rankedSkills.length);
        return;
      }
      for (let count = rankedSkills.length; count >= 1; count--) {
        const overflow = rankedSkills.length - count;
        const reserve = overflow > 0 ? measureOverflowChip(overflow) + CHIP_GAP_PX + MEASURE_BUFFER_PX : 0;
        const available = Math.max(0, rowWidth - reserve);
        if (measureChips(available) >= count) {
          setVisibleCount(count);
          return;
        }
      }
      setVisibleCount(1);
    };
    updateCount();
    const ro = new ResizeObserver(updateCount);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rankedSkills, skillsExpanded]);

  const displaySkills = rankedSkills.slice(0, visibleCount);
  const skillsOverflow = Math.max(0, rankedSkills.length - visibleCount);

  const getRole = () => {
    const topSkills = allSkills.slice(0, 2);
    if (topSkills.length > 0) {
      return `${topSkills.join(' & ')} Talent`;
    }
    return 'Software Talent';
  };

  const SkillChip: React.FC<{ children: React.ReactNode; highlighted?: boolean }> = ({ children, highlighted = false }) => (
    <span
      className={`inline-flex items-center h-[22px] px-[7px] rounded-[5px] whitespace-nowrap ${
        highlighted 
          ? 'bg-zinc-100 dark:bg-slate-800 text-zinc-700 dark:text-slate-300' 
          : 'bg-zinc-50 dark:bg-slate-800/30 text-zinc-700 dark:text-slate-300'
      }`}
      style={{ fontSize: '11.5px', fontWeight: 500 }}
    >
      {children}
    </span>
  );

  // Top schools: prefer bayAreaTalentSignals.evidence.topSchools (array of {name, abbrev}), fallback to pedigreeSignals.topSchool (single)
  const topSchools: { name: string; abbrev: string }[] = (() => {
    const fromEvidence = (talent.bayAreaTalentSignals?.evidence?.topSchools ?? [])
      .filter(s => s?.name?.trim())
      .map(s => ({ name: s.name!.trim(), abbrev: s.abbrev?.trim() || s.name!.trim() }));
    if (fromEvidence.length > 0) return fromEvidence.slice(0, 2);
    const fromPedigree = talent.pedigreeSignals?.topSchool?.trim();
    return fromPedigree ? [{ name: fromPedigree, abbrev: fromPedigree }] : [];
  })();
  const notableCompanies = filterContractOnlyCompanies(
    (talent.pedigreeSignals?.notableCompanies ?? []).map((c) => c?.trim()).filter(Boolean) as string[],
    talent.workExperience ?? [],
  );

  // Title with company context: "Role at Company • X yrs"
  const currentCompany = talent.workExperience?.[0]?.company?.trim() || notableCompanies[0] || null;
  const titleDisplay = (talent.title && talent.title !== 'Unknown Title') ? talent.title : getRole();
  const needsCompany = currentCompany && !titleDisplay.toLowerCase().includes(' at ');
  const titleLine = needsCompany ? `${titleDisplay} at ${currentCompany}` : titleDisplay;

  // Scholar metrics (STEM)
  const scholarCitations = talent.scholarVerify?.citations;
  const scholarHIndex = talent.scholarVerify?.hIndex;
  const scholarI10Index = talent.scholarVerify?.i10Index;
  const hasScholarMetrics = scholarCitations != null || scholarHIndex != null || scholarI10Index != null;

  // GitHub metrics (SWE)
  const ghContributions = talent.githubData?.contributions;
  const ghRepos = talent.githubData?.repos;
  const ghStars = talent.githubData?.stars;
  const ghNonZeroCount = [ghContributions, ghRepos, ghStars].filter(v => v != null && v > 0).length;
  const hasGithubMetrics = ghNonZeroCount >= 2;

  // Check if this is a discarded talent on the search tab (should show greyed out)
  const isDiscardedOnSearch = isDiscarded && activeTab === 'search';

  // Handle card click - open details (action buttons handle save/discard/restore)
  const handleCardClick = () => {
    trackEvent('Candidate Viewed', { candidateId: talent.id, candidateName: talent.name, domain });
    onClick(talent);
  };

  /** Reusable action button with tooltip arrow */
  const ActionBtn = ({ onClick: onBtnClick, disabled, icon, label, activeStyle }: {
    onClick: (e: React.MouseEvent) => void;
    disabled: boolean;
    icon: React.ReactNode;
    label: string;
    activeStyle?: string;
  }) => (
    <div className="relative group/action">
      <button
        onClick={onBtnClick}
        disabled={disabled}
        className={`p-2.5 rounded-lg transition-colors shadow-sm border ${
          activeStyle ?? 'bg-white dark:bg-slate-800 border-zinc-200 dark:border-slate-700 text-zinc-400 hover:text-zinc-600 dark:hover:text-slate-300'
        }`}
      >
        {icon}
      </button>
      <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 px-2 py-1 text-[11px] font-medium text-white bg-zinc-900 dark:bg-slate-700 rounded-md opacity-0 invisible group-hover/action:opacity-100 group-hover/action:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50 shadow-lg">
        {label}
        <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-zinc-900 dark:border-t-slate-700" />
      </span>
    </div>
  );

  return (
    <div
      data-talent-id={talent.id}
      onClick={handleCardClick}
      className={`bg-white dark:bg-slate-900 rounded-xl border border-zinc-200 dark:border-slate-800 hover:border-zinc-300 dark:hover:border-slate-700 hover:shadow-lg transition-all duration-200 flex flex-col h-full group cursor-pointer relative ${isDiscardedOnSearch ? 'opacity-[0.32]' : ''}`}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      {/* Action buttons - visible on mobile, hidden on desktop until hover (or while action in progress) */}
      <div className="absolute top-3 right-3 z-[2]">
        <div className={`flex items-center gap-1.5 transition-all duration-200 ${actionLoading ? '' : 'md:hidden md:group-hover:flex'}`}>
          {activeTab === 'discarded' ? (
            <ActionBtn
              onClick={handleRestore}
              disabled={actionLoading !== null}
              icon={actionLoading === 'restore' ? <span className="inline-block w-4 h-4 border-[2.5px] border-zinc-600 dark:border-slate-300 border-t-transparent rounded-full animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              label="Restore"
            />
          ) : activeTab === 'saved' ? (
            <>
              <ActionBtn
                onClick={handleUnsave}
                disabled={actionLoading !== null}
                icon={actionLoading === 'unsave' ? <span className="inline-block w-4 h-4 border-[2.5px] border-zinc-600 dark:border-slate-300 border-t-transparent rounded-full animate-spin" /> : <Heart className="w-4 h-4 fill-current text-red-500 dark:text-red-400" />}
                label="Unsave"
                activeStyle="bg-white dark:bg-slate-800 border-zinc-200 dark:border-slate-700 text-red-500 dark:text-red-400 hover:text-zinc-600 dark:hover:text-slate-300"
              />
              <ActionBtn
                onClick={handleDiscard}
                disabled={actionLoading !== null}
                icon={actionLoading === 'discard' ? <span className="inline-block w-4 h-4 border-[2.5px] border-zinc-600 dark:border-slate-300 border-t-transparent rounded-full animate-spin" /> : <Ban className="w-4 h-4" />}
                label="Discard"
              />
            </>
          ) : isDiscardedOnSearch ? (
            <>
              <ActionBtn
                onClick={handleSave}
                disabled={actionLoading !== null}
                icon={actionLoading === 'save' ? <span className="inline-block w-4 h-4 border-[2.5px] border-zinc-600 dark:border-slate-300 border-t-transparent rounded-full animate-spin" /> : <Heart className="w-4 h-4" />}
                label="Save"
              />
              <ActionBtn
                onClick={handleRestore}
                disabled={actionLoading !== null}
                icon={actionLoading === 'restore' ? <span className="inline-block w-4 h-4 border-[2.5px] border-zinc-600 dark:border-slate-300 border-t-transparent rounded-full animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                label="Restore"
                activeStyle="bg-white dark:bg-slate-800 border-zinc-200 dark:border-slate-700 text-zinc-600 dark:text-slate-300"
              />
            </>
          ) : (
            <>
              <ActionBtn
                onClick={isSaved ? handleUnsave : handleSave}
                disabled={actionLoading !== null}
                icon={(actionLoading === 'save' || actionLoading === 'unsave') ? <span className="inline-block w-4 h-4 border-[2.5px] border-zinc-600 dark:border-slate-300 border-t-transparent rounded-full animate-spin" /> : <Heart className={`w-4 h-4 ${isSaved ? 'fill-current text-red-500 dark:text-red-400' : ''}`} />}
                label={isSaved ? 'Unsave' : 'Save'}
                activeStyle={isSaved ? 'bg-white dark:bg-slate-800 border-zinc-200 dark:border-slate-700 text-red-500 dark:text-red-400 hover:text-zinc-600 dark:hover:text-slate-300' : undefined}
              />
              <ActionBtn
                onClick={handleDiscard}
                disabled={actionLoading !== null}
                icon={actionLoading === 'discard' ? <span className="inline-block w-4 h-4 border-[2.5px] border-zinc-600 dark:border-slate-300 border-t-transparent rounded-full animate-spin" /> : <Ban className="w-4 h-4" />}
                label="Discard"
              />
            </>
          )}
        </div>
      </div>

      {/* Saved / Viewed badge - hidden on mobile, visible on desktop, fades on hover. Saved takes priority over Viewed. */}
      {!isDiscardedOnSearch && (isSaved || isViewed) && (
        <div className="absolute top-3 right-3 z-[1] hidden md:block md:group-hover:opacity-0 transition-opacity duration-200">
          {isSaved ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-50 dark:bg-red-950/30 text-red-500 dark:text-red-400" style={{ fontSize: '11px', fontWeight: 500 }}>
              <Heart className="w-3 h-3 fill-current" aria-hidden />
              Saved
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-100 dark:bg-slate-800 text-zinc-500 dark:text-slate-400" style={{ fontSize: '11px', fontWeight: 500 }}>
              <Eye className="w-3 h-3" aria-hidden />
              Viewed
            </span>
          )}
        </div>
      )}

      <div className="p-4 flex-1 flex flex-col">
        {/* Name + Verified + Location */}
        <div className="flex items-center gap-3 mb-3">
          <div>
            <div className="flex items-center">
              <span className="inline-flex items-center">
                <span className="font-semibold text-zinc-900 dark:text-slate-100 text-sm leading-tight" style={{ letterSpacing: '-0.01em' }}>
                  {talent.name}
                </span>
                <span className="group/verified relative inline-flex items-center ml-1">
                    <span className="cursor-pointer transition-transform duration-150 ease-out hover:scale-110 active:scale-95">
                      <svg viewBox="0 0 22 22" className="w-[14px] h-[14px] transition-all duration-150" aria-label="Verified account">
                        <g>
                          <circle cx="11" cy="11" r="11" fill="#1D9BF0" className="group-hover/verified:fill-[#1A8CD8]" />
                          <path d="M9.64 15.67L5.47 11.5l1.42-1.42 2.75 2.75 5.67-5.66 1.42 1.41-7.09 7.09z" fill="white" />
                        </g>
                      </svg>
                    </span>
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 px-2 py-1 text-[11px] font-medium text-white bg-zinc-900 dark:bg-slate-700 rounded-md opacity-0 invisible group-hover/verified:opacity-100 group-hover/verified:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50 shadow-lg">
                      Verified profile
                      <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-zinc-900 dark:border-t-slate-700" />
                    </span>
                  </span>
              </span>
            </div>
            {(talent.country || (typeof talent.hourlyRate === 'number' && talent.hourlyRate > 0)) && (
              <div className="flex items-center gap-2 mt-0.5" style={{ fontSize: '12px' }}>
                {typeof talent.hourlyRate === 'number' && talent.hourlyRate > 0 && (
                  <span className="inline-flex items-center font-semibold text-zinc-900 dark:text-slate-100 bg-zinc-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                    ${talent.hourlyRate}/hr
                  </span>
                )}
                {talent.country && (
                  <span className="flex items-center text-zinc-600 dark:text-slate-400">
                    <MapPin className="w-3.5 h-3.5 mr-1" aria-hidden />
                    <span>{talent.country}</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Top Performer */}
        {talent.topPerformer && (
          <div className="mb-1">
            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-500" style={{ fontSize: '12px', fontWeight: 600 }}>
              <Trophy className="w-3.5 h-3.5" aria-hidden />
              Top Performer
            </span>
          </div>
        )}

        {/* CFA */}
        {talent.isCFA && (
          <div className="mb-1">
            <span className="inline-flex items-center gap-1 text-cyan-600 dark:text-cyan-500" style={{ fontSize: '12px', fontWeight: 600 }}>
              <Award className="w-3.5 h-3.5" aria-hidden />
              CFA
            </span>
          </div>
        )}

        {TOP_GITHUB_CONTRIBUTOR_IDS.has(talent.id) && (
          <div className="mb-1">
            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-500" style={{ fontSize: '12px', fontWeight: 600 }}>
              <Github className="w-3.5 h-3.5" aria-hidden />
              Top GitHub Contributor
            </span>
          </div>
        )}

        {/* Title line: "Role at Company • X yrs" */}
        <div className="mb-3">
          <p className="text-[13px] text-zinc-700 dark:text-slate-300">
            {highlightTerms.length > 0 ? highlightFragments(titleLine, highlightTerms) : titleLine}
            {getDomainConfig(domain).showExperience && talent.yearOfExperience != null && (
              <span className="text-zinc-500 dark:text-slate-400">
                {' \u2022 '}{talent.yearOfExperience} yrs
              </span>
            )}
          </p>
        </div>

        {/* Flexible content area */}
        <div className="flex-1 flex flex-col">
          {/* Company & university logos */}
          {(notableCompanies.length > 0 || topSchools.length > 0) && (
            <div className="space-y-1 mb-3">
              {notableCompanies.length > 0 && (
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                  {notableCompanies.slice(0, 2).map((company) => (
                    <div key={company} className="flex items-center gap-1.5 min-w-0">
                      <CompanyLogo company={company} size="sm" />
                      <span className="text-[12px] text-zinc-700 dark:text-slate-300 font-medium">
                        {highlightTerms.length > 0 ? highlightFragments(company, highlightTerms) : company}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {topSchools.length > 0 && (
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                  {topSchools.map((school) => (
                    <div key={school.name} className="flex items-center gap-1.5 min-w-0">
                      <UniversityLogo university={school.name} size="sm" />
                      <TruncatedText text={school.name} className="text-[12px] text-zinc-700 dark:text-slate-300 font-medium" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Scholar metrics (STEM) */}
          {hasScholarMetrics && (
            <div className="mb-3" style={{ fontSize: '12px' }}>
              {scholarCitations != null && (
                <span>
                  <span className="font-semibold text-zinc-900 dark:text-slate-100">{formatCount(scholarCitations)}</span>
                  <span className="text-zinc-600 dark:text-slate-400"> citations</span>
                </span>
              )}
              {scholarHIndex != null && (
                <>
                  {scholarCitations != null && <span className="text-zinc-400 dark:text-slate-500 mx-1.5">&middot;</span>}
                  <span>
                    <span className="text-zinc-600 dark:text-slate-400">h-index </span>
                    <span className="font-semibold text-zinc-900 dark:text-slate-100">{scholarHIndex}</span>
                  </span>
                </>
              )}
              {scholarI10Index != null && (
                <>
                  {(scholarCitations != null || scholarHIndex != null) && <span className="text-zinc-400 dark:text-slate-500 mx-1.5">&middot;</span>}
                  <span>
                    <span className="text-zinc-600 dark:text-slate-400">i10-index </span>
                    <span className="font-semibold text-zinc-900 dark:text-slate-100">{scholarI10Index}</span>
                  </span>
                </>
              )}
            </div>
          )}

          {/* GitHub metrics (SWE) */}
          {hasGithubMetrics && (
            <div className="mb-3 flex items-center flex-wrap gap-x-1 gap-y-0.5" style={{ fontSize: '12px' }}>
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-zinc-600 dark:fill-slate-300 flex-shrink-0" aria-hidden="true">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              {ghContributions != null && ghContributions > 0 && (
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <span className="font-semibold text-zinc-900 dark:text-slate-100">{formatCount(ghContributions)}</span>
                  <span className="text-zinc-500 dark:text-slate-400">contributions</span>
                </span>
              )}
              {ghRepos != null && ghRepos > 0 && (
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <span className="text-zinc-300 dark:text-slate-600">&middot;</span>
                  <span className="font-semibold text-zinc-900 dark:text-slate-100">{formatCount(ghRepos)}</span>
                  <span className="text-zinc-500 dark:text-slate-400">repos</span>
                </span>
              )}
              {ghStars != null && ghStars > 0 && (
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <span className="text-zinc-300 dark:text-slate-600">&middot;</span>
                  <span className="font-semibold text-zinc-900 dark:text-slate-100">{formatCount(ghStars)}</span>
                  <span className="text-zinc-500 dark:text-slate-400">stars</span>
                </span>
              )}
            </div>
          )}

          {/* Skills footer - pinned to bottom */}
          {(rankedSkills.length > 0 || talent.typeOfWork || talent.field) && (
            <div className="mt-auto">
              {/* Type of Work / Expertise tags + STEM field tags */}
              {(talent.typeOfWork || talent.field) && (
                <div className="mb-1">
                  <div className="flex flex-wrap items-center gap-1">
                    {talent.typeOfWork && (
                      <span
                        className="inline-flex items-center h-[20px] px-[6px] rounded-[4px] whitespace-nowrap bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300"
                        style={{ fontSize: '11px', fontWeight: 500 }}
                      >
                        {talent.typeOfWork}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {rankedSkills.length > 0 && (
              <div className="mt-2.5 pt-2.5 border-t border-zinc-100 dark:border-slate-800 overflow-hidden">
                <div
                  ref={skillsRowRef}
                  className={`flex items-start gap-1 ${skillsExpanded ? 'flex-wrap' : 'flex-nowrap overflow-hidden'}`}
                >
                  <div className="flex flex-wrap items-center gap-1 overflow-hidden">
                    {displaySkills.map((skill) => (
                      <SkillChip key={skill} highlighted={isSkillHighlighted(skill)}>
                        {highlightTerms.length > 0 ? highlightFragments(skill, highlightTerms) : skill}
                      </SkillChip>
                    ))}
                  </div>
                  {skillsOverflow > 0 && !skillsExpanded && (
                    <span className="text-zinc-500 dark:text-slate-400 text-[11px] ml-0.5 flex-shrink-0 self-center">
                      +{skillsOverflow}
                    </span>
                  )}
                  {skillsExpanded && skillsOverflow > 0 && (
                    <div className="flex flex-wrap items-center gap-1 w-full mt-1">
                      {rankedSkills.slice(visibleCount).map((skill) => (
                        <SkillChip key={skill} highlighted={isSkillHighlighted(skill)}>
                          {highlightTerms.length > 0 ? highlightFragments(skill, highlightTerms) : skill}
                        </SkillChip>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const SearchResults: React.FC<SearchResultsProps> = ({
  talent,
  loading,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  filters,
  onFiltersChange,
  pagination,
  resultsHighlightTerms = [],
  savedIds = new Set(),
  viewedIds = new Set(),
  discardedIds = new Set(),
  onSave,
  onUnsave,
  onDiscard,
  onRestore,
  activeTab = 'search',
  onCardClick,
  defaultDomainFilter = 'SWE',
  countsByDomain = {},
  hasMoreByDomain = {},
  availableDomains = [],
  multiDomainEnabled = false,
}) => {
  const navigate = useNavigate();
  const resultsRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const multiDomain = multiDomainEnabled;
  
  // Domain filter state for action tabs (domain pills) - initialized from prop
  const [selectedDomainFilter, setSelectedDomainFilter] = React.useState<string>(defaultDomainFilter);

  // Update selected domain when defaultDomainFilter changes (e.g., navigating from STEM search to saved tab)
  useEffect(() => {
    setSelectedDomainFilter(defaultDomainFilter);
  }, [defaultDomainFilter]);

  // Determine hasMore based on active tab and selected domain
  const isActionTab = activeTab !== 'search';
  const effectiveHasMore = isActionTab 
    ? (hasMoreByDomain[selectedDomainFilter] ?? false)
    : hasMore;

  // Infinite scroll: load next page when sentinel enters viewport
  useEffect(() => {
    if (!effectiveHasMore || !onLoadMore || loading || loadingMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // For action tabs, pass the selected domain; for search tab, pass undefined
        if (entry?.isIntersecting) onLoadMore(isActionTab ? selectedDomainFilter : undefined);
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [effectiveHasMore, onLoadMore, loading, loadingMore, isActionTab, selectedDomainFilter]);

  // Terms to highlight in dev card (title, summary, skills)
  // Uses resultsHighlightTerms (skill names, countries, languages from parsed filters)
  // This avoids highlighting common words like "with", "in", "me" from the query
  const highlightTerms = React.useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const add = (t: string) => {
      const k = t.toLowerCase();
      if (!seen.has(k)) { seen.add(k); out.push(t); }
    };
    // Add terms from parsed filters (passed from SearchPage)
    resultsHighlightTerms.forEach(term => { if (term?.trim()) add(term.trim()); });
    // Also add manually selected skills from UI filters
    (filters.skills || []).forEach(s => { if (s?.name?.trim()) add(s.name.trim()); });
    return out;
  }, [resultsHighlightTerms, filters.skills]);

  const handleCardClick = (clickedTalent: SoftwareTalent) => {
    // If onCardClick is provided, use inline view
    if (onCardClick) {
      // Pass the current domain filter for action tabs (for prev/next navigation)
      const domainFilter = activeTab !== 'search' ? selectedDomainFilter : undefined;
      onCardClick(clickedTalent, domainFilter);
      return;
    }

    // Otherwise navigate to detail page
    const pageSize = pagination?.pageSize ?? 18;
    const clickedIndex = talent.findIndex((t) => t.id === clickedTalent.id);
    const pageForClicked =
      clickedIndex >= 0 ? Math.floor(clickedIndex / pageSize) + 1 : pagination?.page ?? 1;
    const domain = filters.domain || 'SWE';
    const tid = localStorage.getItem('tid');
    const tidParam = tid ? `&tid=${tid}` : '';
    navigate(`/candidate/${clickedTalent.id}?domain=${domain}${tidParam}`, {
      state: {
        talent: clickedTalent,
        searchResults: talent,
        totalCount: pagination?.total ?? talent.length,
        filters,
        currentPage: pageForClicked,
        pageSize,
      },
    });
  };

  if (loading) {
    const loadingText = activeTab === 'saved' 
      ? 'Loading saved candidates...'
      : activeTab === 'viewed'
      ? 'Loading viewed candidates...'
      : activeTab === 'discarded'
      ? 'Loading discarded candidates...'
      : 'Searching talent...';
    
    return (
      <div className="flex flex-1 min-h-[60vh] flex-col items-center justify-center text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">{loadingText}</p>
      </div>
    );
  }

  // Helper to get active filter summary for display
  const getActiveFiltersSummary = (): string[] => {
    const parts: string[] = [];
    if (filters.searchQuery && filters.searchQuery.trim().length > 0) {
      parts.push(`Search: "${filters.searchQuery}"`);
    }
    if (filters.countries && filters.countries.length > 0) {
      parts.push(`Location: ${filters.countries.join(', ')}`);
    }
    if (filters.companies && filters.companies.length > 0) {
      parts.push(`Companies: ${filters.companies.join(', ')}`);
    }
    if (filters.yearOfExperience && (filters.yearOfExperience.min !== undefined || filters.yearOfExperience.max !== undefined)) {
      const min = filters.yearOfExperience.min;
      const max = filters.yearOfExperience.max;
      if (min !== undefined && max !== undefined) {
        parts.push(`Experience: ${min}-${max} yrs`);
      } else if (min !== undefined) {
        parts.push(`Experience: ${min}+ yrs`);
      } else if (max !== undefined) {
        parts.push(`Experience: up to ${max} yrs`);
      }
    }
    if (filters.education && filters.education.length > 0) {
      parts.push(`Education: ${filters.education.join(', ')}`);
    }
    if (filters.school && filters.school.trim().length > 0) {
      parts.push(`School: ${filters.school}`);
    }
    if (filters.certificates) {
      parts.push(`Certificates: ${filters.certificates}`);
    }
    if (filters.skills && filters.skills.length > 0) {
      const skillNames = filters.skills.map(s => s.name || 'Unknown').filter(Boolean);
      parts.push(`Skills: ${skillNames.join(', ')}`);
    }
    if (filters.researchPapersCount !== undefined) {
      parts.push(`Research Papers: ${filters.researchPapersCount}+`);
    }
    if (filters.naturalLanguages && filters.naturalLanguages.length > 0) {
      parts.push(`Natural Languages: ${filters.naturalLanguages.join(', ')}`);
    }
    if (filters.programmingLanguages && filters.programmingLanguages.length > 0) {
      parts.push(`Programming Languages: ${filters.programmingLanguages.join(', ')}`);
    }
    return parts;
  };

  if (talent.length === 0) {
    const activeFilters = getActiveFiltersSummary();
    
    // Different empty state messages for each tab
    const getEmptyStateContent = () => {
      switch (activeTab) {
        case 'saved':
          return {
            title: 'No saved candidates',
            description: 'Candidates you save will appear here for easy access.',
            showFilters: false,
          };
        case 'viewed':
          return {
            title: 'No viewed candidates',
            description: 'Candidates you view will appear here to track your browsing history.',
            showFilters: false,
          };
        case 'discarded':
          return {
            title: 'No discarded candidates',
            description: 'Candidates you discard will appear here. You can restore them anytime.',
            showFilters: false,
          };
        default:
          return {
            title: 'No candidates found',
            description: 'Try adjusting your filters or search query to find what you\'re looking for.',
            showFilters: true,
          };
      }
    };
    
    const emptyState = getEmptyStateContent();
    
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 border-dashed">
        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
          <SearchX className="w-8 h-8 text-gray-400 dark:text-gray-500" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
          {emptyState.title}
        </h3>
        {emptyState.showFilters && activeFilters.length > 0 && (
          <div className="mb-3">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Selected filters:
            </p>
            <div className="flex flex-wrap gap-2 justify-center max-w-md">
              {activeFilters.map((filter, idx) => (
                <span key={idx} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded">
                  {filter}
                </span>
              ))}
            </div>
          </div>
        )}
        <p className="text-gray-500 dark:text-gray-400 max-w-sm">
          {emptyState.description}
        </p>
      </div>
    );
  }

  // Get filtered talent based on selected domain
  // When only one domain, show all talent (no domain filtering needed)
  const getFilteredTalent = () => {
    if (!isActionTab) return talent;
    if (!multiDomain) return talent;
    return talent.filter(t => t.domain === selectedDomainFilter);
  };
  const filteredTalent = getFilteredTalent();

  // Render grid for a given list of talent
  const renderTalentGrid = (items: SoftwareTalent[], startIndex: number = 0) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 relative items-stretch">
      {items.map((item, index) => (
        <DeveloperCard
          key={item.id}
          talent={item}
          index={startIndex + index}
          onClick={handleCardClick}
          highlightTerms={highlightTerms}
          isSaved={activeTab === 'saved' || savedIds.has(item.id)}
          isViewed={activeTab === 'viewed' || viewedIds.has(item.id)}
          isDiscarded={discardedIds.has(item.id)}
          onSave={onSave}
          onUnsave={onUnsave}
          onDiscard={onDiscard}
          onRestore={onRestore}
          activeTab={activeTab}
          domain={item.domain || (filters.domain as DomainType) || 'SWE'}
        />
      ))}
    </div>
  );

  // Domain pill tabs for action tabs (counts from API pagination.total)
  const DomainPillTabs = () => (
    <div className="flex justify-center mb-6">
      <div className="flex flex-wrap justify-center gap-1.5 p-1">
        {availableDomains.map(domain => (
          <button
            key={domain}
            onClick={() => {
              setSelectedDomainFilter(domain);
              const params = new URLSearchParams(window.location.search);
              params.set('domain', domain);
              window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
            }}
            className={`px-4 py-1.5 text-sm font-medium rounded-full border transition-all duration-200 ${
              selectedDomainFilter === domain
                ? 'bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-slate-600 shadow-sm'
                : 'bg-gray-50 dark:bg-slate-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-slate-700 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-slate-600'
            }`}
          >
            {getDomainLabel(domain)}
            {(countsByDomain[domain] ?? 0) > 0 && (
              <span className={`ml-1.5 text-xs ${selectedDomainFilter === domain ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
                {countsByDomain[domain]}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex-1 w-full" ref={resultsRef}>
      {/* Domain filter pills for action tabs - only shown when STEM is enabled */}
      {multiDomain && isActionTab && talent.length > 0 && <DomainPillTabs />}
      
      {/* Talent grid */}
      {filteredTalent.length > 0 ? (
        renderTalentGrid(filteredTalent, 0)
      ) : isActionTab && talent.length > 0 ? (
        // Show message when filtered to empty but there are results in other domain
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No {getDomainLabel(selectedDomainFilter)} candidates in this tab.
          </p>
        </div>
      ) : null}

      {/* Sentinel for infinite scroll */}
      {hasMore && <div ref={sentinelRef} className="h-4 flex-shrink-0" aria-hidden="true" />}

      {loadingMore && effectiveHasMore && (
        <div className="flex justify-center py-6">
          <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

export default SearchResults;
